/**
 * Build Itinerary wizard step-state machine (pure logic).
 *
 * The wizard has five steps: Event -> Match Teams -> Follow -> Pitchers ->
 * Assign. The state machine here is deliberately UI-free and pure so it can be
 * unit tested on its own.
 *
 * A step is reachable only once every step before it is complete, so the user
 * cannot skip ahead past work that has not been done (e.g. jumping to Assign
 * before a schedule has been imported at Step 1).
 *
 * Setup mode: a recipient of a distributed schedule already has the event
 * imported, so they start partway through (at step 2) and can never navigate back
 * before that. `minStep` captures the earliest reachable step; it defaults to 1
 * (the create flow, where every step is available).
 */

export type WizardStepId = 1 | 2 | 3 | 4 | 5;

export interface WizardStepDefinition {
  id: WizardStepId;
  /** Short label shown under the stepper circle, e.g. "Event". */
  label: string;
  /** Secondary caption shown under the label, e.g. "Add schedule source". */
  caption: string;
}

/** The five wizard steps in order. */
export const WIZARD_STEPS: readonly WizardStepDefinition[] = [
  { id: 1, label: 'Event', caption: 'Add schedule source' },
  { id: 2, label: 'Match Teams', caption: 'Link to your teams' },
  { id: 3, label: 'Follow', caption: 'Follow players' },
  { id: 4, label: 'Pitchers', caption: 'Who is throwing when' },
  { id: 5, label: 'Assign', caption: 'Assign to staff' },
] as const;

export const FIRST_STEP: WizardStepId = 1;
export const LAST_STEP: WizardStepId = 5;

export interface WizardState {
  /** The step the user is currently viewing. */
  currentStep: WizardStepId;
  /**
   * The highest step the user has completed. Step N is complete once the user
   * has advanced past it; completing a step unlocks step N+1. Starts at 0
   * (nothing completed yet).
   */
  highestCompletedStep: number;
  /**
   * The earliest step reachable in this run. 1 for the create flow (all steps
   * open); higher in setup mode, where earlier steps are already done off-wizard
   * and must never be revisited.
   */
  minStep: WizardStepId;
}

/**
 * The initial wizard state. In the create flow this is Step 1 with nothing
 * completed. In setup mode, pass `minStep` (e.g. 2) to start there with the
 * skipped earlier steps treated as already complete.
 */
export function initialWizardState(minStep: WizardStepId = FIRST_STEP): WizardState {
  return {
    currentStep: minStep,
    highestCompletedStep: minStep - 1,
    minStep,
  };
}

export type StepStatus = 'done' | 'active' | 'upcoming';

/**
 * Classify a step for rendering the stepper: the current step is "active",
 * steps the user has completed are "done", and everything else is "upcoming".
 */
export function stepStatus(step: WizardStepId, state: WizardState): StepStatus {
  if (step === state.currentStep) return 'active';
  if (step <= state.highestCompletedStep) return 'done';
  return 'upcoming';
}

/** The earliest reachable step for a state (defaults to FIRST_STEP for older states). */
function minStepOf(state: WizardState): WizardStepId {
  return state.minStep ?? FIRST_STEP;
}

/**
 * Whether a step can be navigated to directly (e.g. by clicking its stepper
 * header). The current step and any already-completed step are reachable, plus
 * the single next step immediately after the highest completed one. Steps
 * further ahead stay locked until the intervening work is done. Steps before the
 * run's minimum (setup mode's skipped steps) are never reachable.
 */
export function canNavigateToStep(step: WizardStepId, state: WizardState): boolean {
  if (step < minStepOf(state)) return false;
  if (step === state.currentStep) return true;
  return step <= state.highestCompletedStep + 1;
}

/** Whether a Back control should be available from the current step. */
export function canGoBack(state: WizardState): boolean {
  return state.currentStep > minStepOf(state);
}

/**
 * Mark the current step complete and advance to the next one. Completing the
 * final step is a no-op on the step index (there is nowhere further to go), but
 * it still records the completion so the last step reads as "done".
 *
 * Returns a new state; never mutates the input.
 */
export function completeAndAdvance(state: WizardState): WizardState {
  const highestCompletedStep = Math.max(
    state.highestCompletedStep,
    state.currentStep
  );
  const nextStep =
    state.currentStep < LAST_STEP
      ? ((state.currentStep + 1) as WizardStepId)
      : state.currentStep;
  return { ...state, currentStep: nextStep, highestCompletedStep };
}

/** Move back one step without changing completion. No-op at the run's minimum step. */
export function goBack(state: WizardState): WizardState {
  if (!canGoBack(state)) return state;
  return {
    ...state,
    currentStep: (state.currentStep - 1) as WizardStepId,
  };
}

/**
 * Jump to an explicit step, but only when it is reachable (see
 * canNavigateToStep). An unreachable target leaves the state unchanged rather
 * than letting the user skip ahead (or drop below the run's minimum).
 */
export function goToStep(step: WizardStepId, state: WizardState): WizardState {
  if (!canNavigateToStep(step, state)) return state;
  return { ...state, currentStep: step };
}
